import { useState, useEffect, useContext } from "react";
import { ContextAuth } from "../../../contexts/ContextAuth";
import { toast } from "react-toastify";
import { DetailList } from "./DetailList";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import {
    sendCancelPresaleData,
    sendPresaleDeliveredData,
    sendPresaleNotDeliveredData,
    putPresaleInPendingData,
} from "../../../apis/apis.dispatches/apisDispatch";

// ── Status badge ─────────────────────────────────────────────────────────────
const STATUS_STYLES = {
    "04": "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
    "05": "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
    "06": "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    "07": "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300",
    "08": "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
};

function StatusBadge({ status, label }) {
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_STYLES[status] ?? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"}`}>
            {label}
        </span>
    );
}

const thCls = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600";
const tdCls = "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";

export function OrderTrayList({
    setOrders, orders, filename,
    setDetailsFromOrderDispatchReceived, setShowDetailsModal,
    dispatchToConfirm, setDispatchToConfirm, setShowConfirmModal,
    onRefresh,
}) {
    const [items, setItems]       = useState([]);
    const [hostname, setHostname] = useState(null);
    const operationValues         = Object.values(orders);
    const { auth }                = useContext(ContextAuth);

    useEffect(() => {
        if (hostname == null)
            setHostname(window.location.hostname !== "localhost"
                ? "https://www.deldiadistribuciones.nom.pe"
                : "http://192.168.1.22:9017");
    }, [hostname]);

    // Cancel presale when observation changes
    useEffect(() => {
        if ("observation" in dispatchToConfirm && dispatchToConfirm.operationID !== 0) {
            sendCancelPresaleData(dispatchToConfirm)
                .then((r) => {
                    toast.success("Preventa anulada con éxito! " + r.documentNumber);
                    onRefresh?.();
                    setDispatchToConfirm({ ...dispatchToConfirm, observation: "", operationID: 0 });
                })
                .catch(console.error);
        }
    }, [dispatchToConfirm.observation]);

    const handleCancel = (item) => {
        if (!window.confirm("¿Está seguro de anular la preventa?")) return;
        const obs = prompt("Por favor, ingresa el motivo de la anulación", "Anulado por cambio de producto o cancelación de la compra por parte del cliente");
        if (obs != null) setDispatchToConfirm({ ...dispatchToConfirm, observation: obs, operationID: item.operationID });
        else toast.info("Preventa sin observación");
    };

    const handleDelivered = (item) => {
        sendPresaleDeliveredData(item).then(() => {
            toast.success("Entrega realizada con éxito!");
            onRefresh?.();
        });
    };

    const handleNotDelivered = async (item) => {
        const obs = prompt("Por favor, ingresa el motivo de la falla en la entrega", "El cliente no estaba presente al momento de la entrega");
        if (obs != null) {
            item.observation = obs;
            item.userID = auth.data.user_id;
            await sendPresaleNotDeliveredData(item);
            toast.success("Falla en la entrega registrada con éxito!");
            onRefresh?.();
        } else toast.info("Cancelación de entrega sin observación");
    };

    const handlePutPending = (item) => {
        putPresaleInPendingData(item).then(() => {
            toast.success("Entrega puesta en pendiente con éxito!");
            onRefresh?.();
        });
    };

    const handleMarkDownloaded = (item) => {
        const idx = operationValues.findIndex((o) => o.operationID === item.operationID);
        if (idx !== -1) {
            const updated = [...operationValues];
            updated[idx] = { ...updated[idx], background: "success" };
            setOrders(updated);
        }
    };

    if (!orders || orders.length === 0) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                <div className="py-16 text-center">
                    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                        <i className="fas fa-inbox text-gray-400 dark:text-gray-500 text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Sin pedidos</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Aplique los filtros para ver la bandeja de pedidos.</p>
                </div>
            </div>
        );
    }

    const totalAmount = orders.reduce((s, o) => s + o.total, 0);

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">

            {/* Card header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="fas fa-list text-blue-500"></i>
                        Bandeja de pedidos
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{orders.length} registros</p>
                </div>
                <ReactHTMLTableToExcel
                    id="test-table-xls-button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
                    table="table-to-xls"
                    filename={filename}
                    sheet="report"
                    buttonText="↓ Exportar XLS"
                />
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-900">
                <table className="min-w-full text-sm border-separate border-spacing-0" id="table-to-xls">
                    <thead className="sticky top-0 z-10">
                        <tr>
                            <th colSpan={3} className={`${thCls} bg-blue-600 dark:bg-blue-800 text-white`}>Preventa</th>
                            <th colSpan={2} className={`${thCls} bg-cyan-600 dark:bg-cyan-800 text-white`}>Despacho</th>
                            <th colSpan={12} className={`${thCls} bg-gray-600 dark:bg-gray-700 text-white`}>Información General</th>
                        </tr>
                        <tr>
                            {[
                                ["ID","bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"],
                                ["Fecha","bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"],
                                ["Hora","bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"],
                                ["Fecha","bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300"],
                                ["Hora","bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300"],
                                ["Usuario","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Estado","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Código","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Cliente","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Sis. Dist.","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Ruta","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Día","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Dirección","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Serie","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Correlativo","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                                ["Total","bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"],
                                ["Acciones","bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"],
                            ].map(([label, cls]) => (
                                <th key={label + cls} className={`${thCls} ${cls}`}>{label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {operationValues.map((item, i) => (
                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                                <td className={`${tdCls} text-blue-600 dark:text-blue-400 font-medium text-center`}>{item.operationID}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.operationType == "02" ? item.quotationRegisterDate : item.registerDate}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.operationType == "02" ? item.quotationRegisterTime : item.registerTime}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.operationType == "02" ? item.registerDate : ""}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.operationType == "02" ? item.registerTime : ""}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.operationType == "02" ? item.quotationUserName : item.userName}</td>
                                <td className={`${tdCls} text-center`}>
                                    <StatusBadge status={item.dailyRouteStatus} label={item.dailyRouteDisplayStatus} />
                                </td>
                                <td className={`${tdCls} text-center font-bold text-gray-700 dark:text-gray-300`}>{item.clientObservation}</td>
                                <td className={`${tdCls} font-medium text-gray-800 dark:text-gray-200`}>{item.clientName}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.clientPhysicalDistributionDisplay}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.clientGangName}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.clientVisitDayDisplay}</td>
                                <td className={`${tdCls} text-gray-600 dark:text-gray-400`}>{item.clientAddress}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.receiptSerial}</td>
                                <td className={`${tdCls} text-center text-gray-600 dark:text-gray-400`}>{item.receiptNumber}</td>
                                <td className={`${tdCls} text-right font-semibold text-emerald-700 dark:text-emerald-400`}>S/ {Number(item.total).toFixed(2)}</td>
                                <td className={`${tdCls} text-center`}>
                                    <div className="flex items-center justify-center gap-1">
                                        {/* Details */}
                                        <button type="button" title="Ver detalles"
                                            onClick={() => { setDetailsFromOrderDispatchReceived(item.operationDetailDict); setShowDetailsModal(true); }}
                                            className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                        ><i className="fas fa-info-circle text-xs"></i></button>

                                        {item.dailyRouteStatus === "04" && <>
                                            <button type="button" title="Anular" onClick={() => handleCancel(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
                                            ><i className="fas fa-ban text-xs"></i></button>
                                            <button type="button" title="Confirmar pedido"
                                                onClick={() => { setDispatchToConfirm({ ...dispatchToConfirm, clientID: item.clientID, operationID: item.operationID, details: item.operationDetailDict, total: Number(item.total).toFixed(2), documentType: item.clientDocumentType == "01" ? "02" : item.clientDocumentType == "06" ? "03" : "01", physicalDistribution: item.clientPhysicalDistribution }); setShowConfirmModal(true); }}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                                            ><i className="fas fa-check text-xs"></i></button>
                                        </>}

                                        {item.dailyRouteStatus === "05" && <>
                                            <button type="button" title="Entregado" onClick={() => handleDelivered(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                                            ><i className="fas fa-thumbs-up text-xs"></i></button>
                                            <button type="button" title="No entregado" onClick={() => handleNotDelivered(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                                            ><i className="fas fa-thumbs-down text-xs"></i></button>
                                            <a href={`${hostname}/dispatches/print_ticket/${item.operationID}/`} target="_blank" rel="noreferrer" title="Descargar PDF" download onClick={() => handleMarkDownloaded(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                                            ><i className="fas fa-file-pdf text-xs"></i></a>
                                        </>}

                                        {item.dailyRouteStatus === "06" && (
                                            <a href={`${hostname}/dispatches/print_ticket/${item.operationID}/`} target="_blank" rel="noreferrer" title="Descargar PDF" download
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                                            ><i className="fas fa-file-pdf text-xs"></i></a>
                                        )}

                                        {item.dailyRouteStatus === "07" && <>
                                            <button type="button" title="Poner a pendiente" onClick={() => handlePutPending(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                                            ><i className="fas fa-hands-wash text-xs"></i></button>
                                            <button type="button" title="Entregar conforme" onClick={() => handleDelivered(item)}
                                                className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                                            ><i className="fas fa-handshake text-xs"></i></button>
                                        </>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-800 dark:bg-gray-950">
                            <td colSpan={15} className="px-3 py-2 text-xs font-bold text-right text-white uppercase tracking-wide border-t border-gray-600">
                                <i className="fas fa-calculator mr-2"></i>TOTAL
                            </td>
                            <td className="px-3 py-2 text-xs text-right font-bold text-emerald-300 whitespace-nowrap border-t border-gray-600">
                                S/ {Number(totalAmount).toFixed(2)}
                            </td>
                            <td className="border-t border-gray-600 bg-gray-800 dark:bg-gray-950"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <DetailList items={items} />
        </div>
    );
}
